import { store } from 'store'
import { routes } from 'types/routes'
import { activateBetaMode, saveRedirect, saveUrlSearchParam } from 'store/app/slice'
import { UrlSearchParamsKeys } from 'store/app/types'
import { MAGIC_TOKEN_QUERY_PARAM } from 'constants/auth.ts'
import { initializePosthog } from 'utils/tracking.ts'
import { initializeSentry } from 'utils/logging.ts'

/**
 * Executes start up functions
 */
export const startUp = (): void => {
  // Initialize Sentry
  initializeSentry()

  // Initialize Posthog
  initializePosthog()

  // These dispatches must wait for redux-remember: REMEMBER_REHYDRATED carries a snapshot of the whole root state taken
  // at store creation, so anything dispatched before it (url search params, redirect, beta mode) would be reverted by it
  runAfterRehydration(() => {
    // Save url search params if necessary
    saveUrlSearchParamsOnStartup()

    // Save redirect if necessary
    saveRequestedRouteAsRedirectOnStartup()
  })
}

/**
 * Runs the given callback once redux-remember has rehydrated the persisted state, or immediately if it already has
 *
 * @param {() => void} callback
 */
const runAfterRehydration = (callback: () => void): void => {
  if (store.getState().app.storeLoaded) {
    callback()
    return
  }

  const unsubscribe = store.subscribe(() => {
    if (store.getState().app.storeLoaded) {
      unsubscribe()
      callback()
    }
  })
}

/**
 * Saves the requested route as redirect in store on startup
 */
const saveRequestedRouteAsRedirectOnStartup = (): void => {
  const { pathname, search } = window.location

  const betaMode = new URLSearchParams(window.location.search).get('beta') === 'true'

  if (betaMode) {
    store.dispatch(activateBetaMode())
  }

  // Only save protected route paths as a redirect that are not /home
  if (
    ![
      '/',
      '/superuser',

      // Auth routes
      routes.LOGIN,
      routes.LOGIN_SALES,
      routes.LOGIN_SUPER_USER,
      routes.REGISTER,
      routes.REQUEST_NEW_PASSWORD,
      routes.REQUEST_NEW_PASSWORD_SALES,
      routes.REQUEST_NEW_PASSWORD_SUPER_USER,
      // activate & reset routes are checked with an includes() below because of the variable :token

      // Home views
      routes.ACCOUNT_SELECTION,
      routes.SALES_HOME,
      routes.CUSTOMER_EMAIL
    ].includes(pathname) &&
    !pathname.includes('activate') &&
    !pathname.includes('reset')
  ) {
    // Never save the magic token as part of the redirect: it is consumed by the exchange on landing, so navigating
    // back to a URL that still contains it would trigger a second exchange of an already-used token.
    const searchParams = new URLSearchParams(search)
    searchParams.delete(MAGIC_TOKEN_QUERY_PARAM)
    const cleanSearch = searchParams.toString()

    store.dispatch(saveRedirect(`${pathname}${cleanSearch ? `?${cleanSearch}` : ''}`))
  }
}

/**
 * Checks the URL search params & saves them to the store if necessary
 */
const saveUrlSearchParamsOnStartup = () => {
  // Fetch the URL params
  const search = new URLSearchParams(window.location.search)

  Object.values(UrlSearchParamsKeys).forEach((key) => {
    if (search.has(key)) {
      store.dispatch(saveUrlSearchParam({ key, value: search.get(key) ?? '' }))
    }
  })
}
